/**
 * Reordering and reparenting in the Outliner, as pure functions: which part
 * of a row a drag is over, where a drop puts things, the one-step edit that
 * moves them (`entity.move`, which keeps world transforms), and the Alt+arrow
 * equivalents for the keyboard.
 *
 * Destinations name the sibling to go in front of rather than an index, so a
 * run of moves (several dragged entities) stays correct as each one lands.
 */
import type { CommandOrigin, CommandRequest } from "../../commands/types";
import type { EntityId, EntityRecord } from "../../world/entity";
import { isLevelRoot, subtreeOf, topmostOf } from "../../world/hierarchy";
import type { Edit } from "../selection/selectionEdits";

type Records = ReadonlyMap<EntityId, EntityRecord>;

export type DropZone = "before" | "after" | "onto";

export interface Destination {
  /** The new parent, or null for the top level. */
  readonly parent: EntityId | null;
  /** The sibling to go in front of, or null for the end. */
  readonly before: EntityId | null;
}

/** Share of a row's height at its top and bottom that means "beside" rather than "onto". */
const EDGE = 0.25;

/** Top and bottom quarters drop beside the row; the middle drops onto it, as in Unreal's Outliner. */
export function dropZone(offsetY: number, height: number): DropZone {
  if (offsetY < height * EDGE) return "before";
  if (offsetY > height * (1 - EDGE)) return "after";
  return "onto";
}

export function dropDestination(entities: Records, target: EntityId, zone: DropZone): Destination {
  if (zone === "onto") return { parent: target, before: null };

  const parent = entities.get(target)?.parent ?? null;
  if (zone === "before") return { parent, before: target };
  const siblings = siblingIds(entities, parent);
  return { parent, before: siblings[siblings.indexOf(target) + 1] ?? null };
}

/**
 * Moves `ids` to `destination` as one step: each topmost one, never the level
 * root or into its own subtree, and not if it's already there. Null when
 * nothing would move.
 */
export function moveEdit(
  entities: Records,
  ids: readonly EntityId[],
  { parent, before }: Destination,
  origin: CommandOrigin,
): Edit | null {
  const movable = topmostOf(entities, ids).filter((id) => {
    const record = entities.get(id);
    if (!record || isLevelRoot(record)) return false;
    return parent === null || !subtreeOf(entities, id).includes(parent);
  });

  // Moved entities leave their places, so "in front of" skips past any of them.
  const siblings = siblingIds(entities, parent);
  const moving = new Set(movable);
  const anchor = before === null ? null : firstFrom(siblings, before, (id) => !moving.has(id));
  if (alreadyThere(siblings, movable, anchor)) return null;

  const requests: CommandRequest[] = movable.map((entity) => ({
    type: "entity.move",
    payload: { entity, parent, ...(anchor !== null && { before: anchor }) },
    origin,
  }));
  const [only] = requests;
  if (!only) return null;
  if (requests.length === 1) return only;
  return { label: `Move ${requests.length} entities`, requests };
}

/** Alt+Up/Down swap with a sibling; Alt+Right indents under the one above; Alt+Left outdents. */
export function keyboardMove(entities: Records, id: EntityId, key: string): Destination | null {
  const record = entities.get(id);
  if (!record) return null;
  const siblings = siblingIds(entities, record.parent);
  const index = siblings.indexOf(id);
  const above = siblings[index - 1];

  switch (key) {
    case "ArrowUp":
      return above === undefined ? null : { parent: record.parent, before: above };
    case "ArrowDown": {
      if (index === siblings.length - 1) return null;
      return { parent: record.parent, before: siblings[index + 2] ?? null };
    }
    case "ArrowRight":
      return above === undefined ? null : { parent: above, before: null };
    case "ArrowLeft":
      return outdent(entities, record);
    default:
      return null;
  }
}

/** Out to the grandparent, right after the parent; children of the level root stay in the level. */
function outdent(entities: Records, record: EntityRecord): Destination | null {
  const parent = record.parent === null ? undefined : entities.get(record.parent);
  if (!parent || isLevelRoot(parent)) return null;
  return dropDestination(entities, parent.id, "after");
}

function siblingIds(entities: Records, parent: EntityId | null): readonly EntityId[] {
  if (parent !== null) return entities.get(parent)?.children ?? [];
  return [...entities.values()].filter((record) => record.parent === null).map((r) => r.id);
}

/** The first id at or after `from` in `ids` that passes `test`, or null. */
function firstFrom(
  ids: readonly EntityId[],
  from: EntityId,
  test: (id: EntityId) => boolean,
): EntityId | null {
  return ids.slice(Math.max(0, ids.indexOf(from))).find(test) ?? null;
}

/** Whether the movers already sit together, in order, right in front of `anchor` (or at the end). */
function alreadyThere(
  siblings: readonly EntityId[],
  movers: readonly EntityId[],
  anchor: EntityId | null,
): boolean {
  if (movers.length === 0) return true;
  const end = anchor === null ? siblings.length : siblings.indexOf(anchor);
  const run = siblings.slice(end - movers.length, end);
  return run.length === movers.length && run.every((id, i) => id === movers[i]);
}
